import { monotonePath } from '../../utils/monotonePath';
import { PMC_COLORS } from './palette';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChartScrub } from '../../hooks/useChartScrub';
import { formatShortDate } from '../../i18n/formatters';
import { displayDigits } from '../../utils/displayDigits';
import { buildPeriodicLines, type RawPoint } from './periodicChartBuilder';
import { mergePmcSettings } from './pmcSettings';
import { ChartPeriodic, type ChartPeriodicProps } from './ChartPeriodic';
import { pmcFormDomain, pmcLoadDomain, nearestPmcTime } from './pmcSplitModel';
import './pmc-split.css';
import './chart-series-line.css';

const { ctl: BLUE, atl: PINK, tsbPositive: GREEN, tsbNegative: RED } = PMC_COLORS;

/** Shared-date PMC: load and fatigue share a scale, form has its own zero-centred panel. */
export function ChartPmcSplit(props: ChartPeriodicProps) {
  const { t, i18n } = useTranslation();
  const settings = useMemo(() => mergePmcSettings(props.settings), [props.settings]);
  const host = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const clip = useId().replace(/:/g, '');
  const [width, setWidth] = useState(360);
  const [selected, setSelected] = useState<number | null>(null);
  useEffect(() => {
    if (!host.current) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(240, entry.contentRect.width)));
    observer.observe(host.current);
    return () => observer.disconnect();
  }, [settings.pmc?.show_pmc]);
  const series = useMemo(() => buildPeriodicLines(props.data, settings).map(line => ({
    ...line, points: line.points.filter(point => Number.isFinite(point.x) && Number.isFinite(point.y)
      && (!props.dateRange || (point.startDate >= props.dateRange.start && point.startDate <= props.dateRange.end))),
  })), [props.data, settings, props.dateRange]);
  const ctl = series.find(line => line.label.includes('CTL'));
  const atl = series.find(line => line.label.includes('ATL'));
  const tsb = series.find(line => line.label.includes('TSB'));
  const times = useMemo(() => [...new Set([ctl, atl, tsb].flatMap(line => line?.points.map(point => point.x) ?? []))].sort((a, b) => a - b), [ctl, atl, tsb]);
  const current = times.includes(selected ?? NaN) ? selected! : times[times.length - 1];
  const start = times[0] ?? 0;
  const end = times[times.length - 1] ?? start;
  const left = 34, right = width - 10, plotWidth = right - left;
  const height = props.fullscreen ? 490 : 350;
  const top = 28, loadHeight = props.fullscreen ? 220 : 140;
  const formTop = top + loadHeight + 46, formHeight = props.fullscreen ? 145 : 100;
  const bottom = formTop + formHeight;
  const toX = (time: number) => start === end ? (left + right) / 2 : left + (time - start) / (end - start) * plotWidth;
  const loadDomain = pmcLoadDomain([ctl, atl].flatMap(line => line ? line.points.map(p => p.y) : []));
  const formDomain = pmcFormDomain(tsb?.points.map(p => p.y) ?? []);
  const loadY = (value: number) => top + loadHeight - value / loadDomain.max * loadHeight;
  const formY = (value: number) => formTop + formHeight - (value - formDomain.min) / (formDomain.max - formDomain.min) * formHeight;
  const zero = formY(0);
  const date = (time: number) => Number.isFinite(time) ? displayDigits(formatShortDate(new Date(time).toISOString().slice(0, 10), i18n.language), i18n.language) : '—';
  const number = (value: number) => displayDigits(String(Math.round(value)), i18n.language);
  const pointAt = (points?: RawPoint[]) => points?.find(point => point.x === current);
  const values = [
    { key: 'CTL', line: ctl, color: BLUE, label: t('charts.pmcSeries.fitness') },
    { key: 'ATL', line: atl, color: PINK, label: t('charts.pmcSeries.fatigue') },
    { key: 'TSB', line: tsb, color: GREEN, label: t('charts.pmcSplit.form') },
  ];
  const scrub = useChartScrub(times.length > 0, clientX => {
    const bounds = svg.current?.getBoundingClientRect();
    if (!bounds) return;
    const x = (clientX - bounds.left) / bounds.width * width;
    const time = start + Math.max(0, Math.min(1, (x - left) / plotWidth)) * (end - start);
    setSelected(nearestPmcTime(times, time));
  });
  const linePath = (points: RawPoint[], toY: (y: number) => number) => monotonePath(points.map(point => ({ x: toX(point.x), y: toY(point.y) })));
  const formPoints = tsb?.points ?? [];
  const formPath = linePath(formPoints, formY);
  const formArea = formPoints.length ? `${formPath} L${toX(formPoints[formPoints.length - 1].x)},${zero} L${toX(formPoints[0].x)},${zero} Z` : '';
  if (settings.pmc?.show_pmc === false) return <ChartPeriodic {...props} />;
  return <div ref={host} className="pmc-split" style={props.style}>
    <div className="pmc-split__readings" aria-live="polite" aria-atomic="true">
      {values.map(item => {
        const point = pointAt(item.line?.points);
        return <div key={item.key} className="pmc-split__reading">
          <span>{item.label} <bdi>{item.key}</bdi></span>
          <strong style={{ color: point && point.y < 0 ? RED : item.color }}><bdi>{point ? number(point.y) : '—'}</bdi></strong>
        </div>;
      })}
    </div>
    <div className="pmc-split__date">{date(current)}</div>
    {times.length ? <svg ref={svg} className="pmc-split__plot" viewBox={`0 0 ${width} ${height}`} style={{ height }}
      role="slider" tabIndex={0} aria-label={t('charts.pmcSplit.inspect')}
      aria-valuemin={0} aria-valuemax={Math.max(0, times.length - 1)} aria-valuenow={Math.max(0, times.indexOf(current))}
      aria-valuetext={`${date(current)} · ${values.map(item => `${item.label} ${pointAt(item.line?.points)?.y ?? '—'}`).join(' · ')}`}
      onKeyDown={event => {
        let index = times.indexOf(current);
        if (event.key === 'ArrowLeft') index--;
        else if (event.key === 'ArrowRight') index++;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = times.length - 1;
        else return;
        event.preventDefault(); setSelected(times[Math.max(0, Math.min(times.length - 1, index))]);
      }} {...scrub}>
      <defs>
        <clipPath id={`${clip}-upper`}><rect x={left} y={top} width={plotWidth} height={loadHeight} /></clipPath>
        <clipPath id={`${clip}-positive`}><rect x={left} y={formTop} width={plotWidth} height={zero - formTop} /></clipPath>
        <clipPath id={`${clip}-negative`}><rect x={left} y={zero} width={plotWidth} height={bottom - zero} /></clipPath>
      </defs>
      <text className="pmc-split__caption" x={right} y={14} textAnchor="end">{t('charts.pmcSplit.load')}</text>
      {[0, .5, 1].map(f => <g key={`load-${f}`}>
        <line className="pmc-split__grid" x1={left} x2={right} y1={loadY(f * loadDomain.max)} y2={loadY(f * loadDomain.max)} />
        <text className="pmc-split__tick" x={left - 6} y={loadY(f * loadDomain.max) + 4} textAnchor="end">{number(f * loadDomain.max)}</text>
      </g>)}
      {[{ line: ctl, color: BLUE }, { line: atl, color: PINK }].map(({ line, color }) => line && <g key={line.label} clipPath={`url(#${clip}-upper)`}>
        <path className="chart-series-line" d={linePath(line.points, loadY)} stroke={color} />
        {pointAt(line.points) && <circle cx={toX(current)} cy={loadY(pointAt(line.points)!.y)} r={3.5} fill={color} stroke="var(--fg-1)" />}
      </g>)}
      <text className="pmc-split__caption" x={right} y={formTop - 14} textAnchor="end">{t('charts.pmcSplit.form')} · TSB</text>
      <rect x={left} y={formTop} width={plotWidth} height={zero - formTop} fill={GREEN} opacity={.035} />
      <rect x={left} y={zero} width={plotWidth} height={bottom - zero} fill={RED} opacity={.035} />
      {[formDomain.min, 0, formDomain.max].map(value => <g key={`form-${value}`}>
        <line className={value === 0 ? 'pmc-split__zero' : 'pmc-split__grid'} x1={left} x2={right} y1={formY(value)} y2={formY(value)} />
        <text className="pmc-split__tick" style={value < 0 ? { fill: RED } : undefined} x={left - 6} y={formY(value) + 4} textAnchor="end">{number(value)}</text>
      </g>)}
      {[
        { key: 'positive', color: GREEN }, { key: 'negative', color: RED },
      ].map(({ key, color }) => <g key={key} clipPath={`url(#${clip}-${key})`}>
        {settings.pmc?.show_form_TSB_fill !== false && <path d={formArea} fill={color} opacity={.12} />}
        <path className="chart-series-line" d={formPath} stroke={color} />
      </g>)}
      <line className="pmc-split__cursor" x1={toX(current)} x2={toX(current)} y1={top} y2={bottom} />
      {pointAt(formPoints) && <circle cx={toX(current)} cy={formY(pointAt(formPoints)!.y)} r={3.5} fill={pointAt(formPoints)!.y < 0 ? RED : GREEN} stroke="var(--fg-1)" />}
      {(start === end ? [0] : plotWidth >= 324 ? [0, .5, 1] : [0, 1]).map(f => <text key={`time-${f}`} className="pmc-split__tick" x={start === end ? toX(start) : left + plotWidth * f} y={bottom + 24} textAnchor={start === end ? 'middle' : f === 0 ? 'start' : f === 1 ? 'end' : 'middle'}>{date(start + (end - start) * f)}</text>)}
    </svg> : <p className="pmc-split__empty">{t('records.empty')}</p>}
  </div>;
}
